import { inject, Injectable } from '@angular/core';
import { ENVIRONMENT } from '@ld.consumer-portal/models';
import {
  AnalyticsService,
  AuthService,
  BrowserService,
  LoggingService,
  PlatformService,
  ThemeService,
} from '@ld.consumer-portal/utils';
import { DashboardService } from '../dashboard/dashboard.service';
import { MlaAuthService } from './mla-auth.service';
import { MlaBorrowerService } from './mla-borrower.service';

@Injectable({ providedIn: 'root' })
export class MlaRedirectService {
  private readonly env = inject(ENVIRONMENT);
  private readonly dashboardService = inject(DashboardService);
  private readonly mlaAuthService = inject(MlaAuthService);
  private readonly mlaBorrowerService = inject(MlaBorrowerService);
  private readonly authService = inject(AuthService);
  private readonly themeService = inject(ThemeService);
  private readonly platformService = inject(PlatformService);
  private readonly browserService = inject(BrowserService);
  private readonly loggingService = inject(LoggingService);
  private readonly analyticsService = inject(AnalyticsService);

  async redirectToMlaGetStarted(productId: string): Promise<void> {
    const mlaWebUrl = this.env.appSettings?.mlaWebUrl;
    if (!mlaWebUrl) {
      this.loggingService.logWarning('[MlaRedirectService] mlaWebUrl not configured in environment');
      return;
    }

    const newTab = this.platformService.isNativeMobile() ? null : window.open('about:blank', '_blank');

    this.analyticsService.pushEvent({
      event: 'product_clicked',
      productId,
      action: 'mla_redirect',
    });

    try {
      let refId: string | null = null;
      try {
        refId = await this.dashboardService.getRecentBankerRefId();
      } catch (error) {
        this.loggingService.logException(error, '[MlaRedirectService] Failed to get recent banker');
      }

      const redirectUrl = this.buildMlaGetStartedUrl(mlaWebUrl, productId, refId);
      await this.openInNewTab(redirectUrl, newTab);
    } catch (error) {
      this.loggingService.logException(error, '[MlaRedirectService] Error redirecting to MLA');
      const redirectUrl = this.buildMlaGetStartedUrl(mlaWebUrl, productId, null);
      await this.openInNewTab(redirectUrl, newTab);
    }
  }

  private buildMlaGetStartedUrl(mlaWebUrl: string, productId: string, refId?: string | null): string {
    const loanType = productId === 'home-purchase' ? 'Purchase' : 'Refinance';
    const appSource = this.env.appSettings?.appSource;
    const userProfile = this.dashboardService.userProfile();

    const params = new URLSearchParams();
    params.append('entra', 'true');
    params.append('loantype', loanType);

    if (appSource) {
      params.append('appsource', appSource);
    }

    if (refId) {
      params.append('refid', refId);
    }

    const selectedBorrower = this.mlaAuthService.getSelectedBorrowerName();
    const cachedBorrower = this.mlaBorrowerService.getCachedPrimaryBorrower();

    const firstName = selectedBorrower?.firstName || cachedBorrower?.firstName || userProfile?.firstName;
    const lastName = selectedBorrower?.lastName || cachedBorrower?.lastName || userProfile?.lastName;
    const email = userProfile?.email || this.authService.getUserEmail() || cachedBorrower?.email;
    const phone =
      userProfile?.phone ||
      userProfile?.phoneNumber ||
      this.mlaAuthService.getSelectedBorrowerPhoneNumbers()?.[0] ||
      cachedBorrower?.phone;

    if (firstName) {
      params.append('fname', firstName);
    }
    if (lastName) {
      params.append('lname', lastName);
    }
    if (email) {
      params.append('email', email);
    }
    if (phone) {
      params.append('phone', phone);
    }

    if (this.themeService.isJvThemeActive()) {
      const xPartnerId = this.themeService.getXPartnerId();
      if (xPartnerId && xPartnerId !== 'loanDepot') {
        params.append('jv', `${xPartnerId}.mla`);
      }
    }

    return `${mlaWebUrl}/register/get-started?${params.toString()}`;
  }

  private async openInNewTab(url: string, newTab: Window | null): Promise<void> {
    if (this.platformService.isNativeMobile()) {
      await this.browserService.openUrl(url);
      return;
    }

    if (newTab) {
      newTab.opener = null;
      newTab.location.href = url;
    } else {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  }
}
